import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Charge } from '../types/entities';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

interface ChargeState {
  charges: Charge[];
  charge: Charge | null;
  chargesByLocal: Charge[];
  chargesByLease: Charge[];
  loading: boolean;
  error: string | null;
}

const initialState: ChargeState = {
  charges: [],
  charge: null,
  chargesByLocal: [],
  chargesByLease: [],
  loading: false,
  error: null,
};



/* ============================
   THUNKS
============================ */

export const fetchCharges = createAsyncThunk<
  Charge[],
  void,
  { state: RootState }
>('charge/fetchCharges', async (_, { getState, rejectWithValue }) => {
  try {
    const { authentification } = getState();
    const response = await axios.get(
      `${baseUrlApi}/charges`,
      {
        headers: {
          Authorization: `bearer ${authentification.user.token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch charges'
    );
  }
});

export const fetchCharge = createAsyncThunk<
  Charge,
  {  LocalId: number },
  { state: RootState }
>(
  'charge/fetchCharge',
  async ({  LocalId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(
        `${baseUrlApi}/charges/local/${LocalId}`,
        {
          headers: {
            Authorization: `bearer ${authentification.user.token}`,
          },
        }
      );
      console.log('success WHILE fetching Charge', response);
      dispatch(fetchCharges());
      return response.data;
    } catch (error: any) {
      console.log('ERROR WHILE FETCHING CHARGES', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch charge'
      );
    }
  }
);

// ✅ createCharge (sans 's') - Version standard
export const createCharge = createAsyncThunk<
  Charge,
  Partial<Charge>,
  { state: RootState }
>('charge/createCharge', async (chargeData, { getState, rejectWithValue }) => {
  try {
    const { authentification } = getState();
    const response = await axios.post(
      `${baseUrlApi}/charges`,
      chargeData,
      {
        headers: {
          Authorization: `bearer ${authentification.user.token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.log('ERROR WHILE CREATING CHARGES', error);
    return rejectWithValue(
      error.response?.data?.message || 'Failed to create charge'
    );
  }
});

// ✅ createCharges (avec 's') - Version FormData pour compatibilité
export const createCharges = createAsyncThunk<
  Charge,
  FormData | Partial<Charge>,
  { state: RootState }
>('charge/createCharges', async (chargeData, { getState, rejectWithValue }) => {
  try {
    const { authentification } = getState();
    
    // Déterminer si c'est du FormData ou un objet simple
    const isFormData = chargeData instanceof FormData;
    
    const response = await axios.post(
      `${baseUrlApi}/charges`,
      chargeData,
      {
        headers: {
          // Ne pas forcer Content-Type pour FormData : axios l'ajoute automatiquement
          // avec le bon boundary (multipart/form-data; boundary=...)
          ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          Authorization: `Bearer ${authentification.user.token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.log('ERROR WHILE CREATING CHARGES', error);
    return rejectWithValue(
      error.response?.data?.message || 'Failed to create charge'
    );
  }
});

export const updateCharge = createAsyncThunk<
  Charge,
  { chargeId: number; chargeData: Partial<Charge> },
  { state: RootState }
>(
  'charge/updateCharge',
  async ({ chargeId, chargeData }, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.put(
        `${baseUrlApi}/charges/${chargeId}`,
        chargeData,
        {
          headers: {
            Authorization: `bearer ${authentification.user.token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update charge'
      );
    }
  }
);

export const deleteCharge = createAsyncThunk<
  number,
  number,
  { state: RootState }
>('charge/deleteCharge', async (chargeId, { getState, rejectWithValue }) => {
  try {
    const { authentification } = getState();
    await axios.delete(`${baseUrlApi}/charges/${chargeId}`, {
      headers: {
        Authorization: `bearer ${authentification.user.token}`,
      },
    });
    console.log('success WHILE delete Charge');
    return chargeId;
  } catch (error: any) {
    console.log('ERROR WHILE delete Charge', error);
    return rejectWithValue(
      error.response?.data?.message || 'Failed to delete charge'
    );
  }
});

export const fetchChargesByLocal = createAsyncThunk<
  Charge[],
  number,
  { state: RootState }
>(
  'charge/fetchChargesByLocal',
  async (BailId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(
        `${baseUrlApi}/charges/local/${BailId}`,
        {
          headers: {
            Authorization: `bearer ${authentification.user.token}`,
          },
        }
      );
      console.log('success WHILE Call payment', response);
      dispatch(fetchCharges());
      return response.data;
    } catch (error: any) {
      console.log('ERROR WHILE FETCHING Payment', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch charges by local'
      );
    }
  }
);

export const fetchChargesByLease = createAsyncThunk<
  Charge[],
  number,
  { state: RootState }
>(
  'charge/fetchChargesByLease',
  async (BailId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(
        `${baseUrlApi}/charges/locallease/${BailId}`,
        {
          headers: {
            Authorization: `bearer ${authentification.user.token}`,
          },
        }
      );
      console.log('success WHILE Call payment', response);
      dispatch(fetchCharges());
      return response.data;
    } catch (error: any) {
      console.log('ERROR WHILE FETCHING Payment', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch charges by lease'
      );
    }
  }
);

/* ============================
   SLICE
============================ */

const chargeSlice = createSlice({
  name: 'charge',
  initialState,
  reducers: {
    clearCharge: (state) => {
      state.charge = null;
    },
    clearCharges: (state) => {
      state.charges = [];
    },
    resetChargeState: () => initialState,
  },
  extraReducers: (builder) => {
    // Gestion des états de chargement pour tous les thunks
    [
      fetchCharges,
      fetchCharge,
      createCharge,
      createCharges, // ✅ Ajouté
      updateCharge,
      deleteCharge,
      fetchChargesByLocal,
      fetchChargesByLease,
    ].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    });

    // Cas fulfilled spécifiques
    builder
      .addCase(fetchCharges.fulfilled, (state, action) => {
        state.loading = false;
        state.charges = action.payload;
      })
      .addCase(fetchCharge.fulfilled, (state, action) => {
        state.loading = false;
        state.charge = action.payload;
      })
      .addCase(createCharge.fulfilled, (state, action) => {
        state.loading = false;
        state.charges.push(action.payload);
      })
      .addCase(createCharges.fulfilled, (state, action) => {
        state.loading = false;
        state.charges.push(action.payload);
      })
      .addCase(updateCharge.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.charges.findIndex(
          (c) => c.id === action.payload.id
        );
        if (index !== -1) state.charges[index] = action.payload;
      })
      .addCase(deleteCharge.fulfilled, (state, action) => {
        state.loading = false;
        state.charges = state.charges.filter((item) => item.id !== action.payload);
        state.chargesByLocal = state.chargesByLocal.filter((item) => item.id !== action.payload);
      })
      .addCase(fetchChargesByLocal.fulfilled, (state, action) => {
        state.loading = false;
        state.chargesByLocal = action.payload;
      })
      .addCase(fetchChargesByLease.fulfilled, (state, action) => {
        state.loading = false;
        state.chargesByLease = action.payload;
      });
  },
});

export const { clearCharge, clearCharges, resetChargeState } =
  chargeSlice.actions;

export default chargeSlice.reducer;
